/**
 * BCSubtypeOnboardingPrompt — iter1018
 *
 * Мек onboarding нudge за жени, който се появява ЕДНОКРАТНО на Dashboard
 * след регистрация: "Имате ли диагноза рак на гърдата?" Отговорите ("Не",
 * "Да", "Ще реша по-късно") се записват в бекенда, така че prompt-а
 * повече не се показва.
 *
 * Ако "Да" → пренасочва към BHI страницата (BCSubtypePanel) за да декларира.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart, ShieldCheck, Clock } from "lucide-react";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BCSubtypeOnboardingPrompt() {
  const { user, language } = useAuth();
  const navigate = useNavigate();
  const bg = (language || "bg") === "bg";

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!user) return;
      // We only ever want to check ONCE per session; the backend enforces
      // real idempotence, but this avoids nagging network calls.
      const sessionKey = "bc_subtype_prompt_checked";
      if (sessionStorage.getItem(sessionKey) === "1") return;
      // Impersonation should not see the prompt — the admin/doctor is not
      // the patient and shouldn't be answering diagnostic questions.
      if (localStorage.getItem("impersonation_active") === "true") {
        sessionStorage.setItem(sessionKey, "1");
        return;
      }
      try {
        const res = await axios.get(`${API}/bc-subtypes/onboarding-prompt`);
        if (cancelled) return;
        if (res.data?.should_prompt === true) {
          setOpen(true);
        }
      } catch {
        /* silent — non-critical UX */
      } finally {
        sessionStorage.setItem(sessionKey, "1");
      }
    }
    check();
    return () => { cancelled = true; };
  }, [user]);

  const dismiss = async (action) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/bc-subtypes/onboarding-prompt/dismiss`, { action });
    } catch { /* proceed anyway */ }
    setSubmitting(false);
    setOpen(false);
    if (action === "declared") {
      navigate("/dashboard/breast-health");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-lg bg-white dark:bg-[hsl(222,47%,10%)] text-stone-900 dark:text-gray-100 border-stone-200 dark:border-white/10"
        data-testid="bc-subtype-onboarding-prompt"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Heart className="w-5 h-5 text-fuchsia-500" />
            {bg ? "Един бърз въпрос за здравето на гърдите" : "One quick breast-health question"}
          </DialogTitle>
          <DialogDescription className="text-sm opacity-80 pt-2 leading-relaxed">
            {bg
              ? "Задаваме го веднъж, за да персонализираме анализа на храненето Ви. Отговорите Ви са лични и не се споделят."
              : "We ask this once so we can personalise your nutrition analysis. Your answer is private."}
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm font-semibold mt-2">
          {bg
            ? "Имате ли поставена медицинска диагноза рак на гърдата?"
            : "Do you have a confirmed breast cancer diagnosis?"}
        </div>

        <div className="grid grid-cols-1 gap-2 mt-3">
          <Button
            onClick={() => dismiss("declared")}
            disabled={submitting}
            data-testid="bc-onboarding-yes-btn"
            className="justify-start bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-semibold"
          >
            <Heart className="w-4 h-4 mr-2" />
            {bg
              ? "Да — искам да декларирам подтипа"
              : "Yes — I want to declare my subtype"}
          </Button>
          <Button
            onClick={() => dismiss("not_diagnosed")}
            disabled={submitting}
            data-testid="bc-onboarding-no-btn"
            variant="outline"
            className="justify-start"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            {bg ? "Не — не съм диагностицирана" : "No — I have no diagnosis"}
          </Button>
          <Button
            onClick={() => dismiss("not_now")}
            disabled={submitting}
            data-testid="bc-onboarding-later-btn"
            variant="ghost"
            className="justify-start opacity-70"
          >
            <Clock className="w-4 h-4 mr-2" />
            {bg ? "Ще реша по-късно" : "I'll decide later"}
          </Button>
        </div>

        <p className="text-[11px] opacity-60 mt-3 leading-relaxed">
          {bg
            ? "Може по-всяко време да промените или изчистите декларацията от Breast Health Intelligence."
            : "You can change or clear this declaration any time from Breast Health Intelligence."}
        </p>
      </DialogContent>
    </Dialog>
  );
}
